import { audioRecorder } from '@/pages/DigitalHuman/util/audio-recorder'
import { getDigitalStore } from '@/store/digital'
import { useEffect, useRef, useState } from 'react'

interface UseAudioRecordingOptions {
  transcribeAudio?: (blob: Blob) => Promise<string>
  onTranscriptionComplete?: (text: string) => void
}

export function useAudioRecording({
  transcribeAudio,
  onTranscriptionComplete,
}: UseAudioRecordingOptions) {
  const [isListening, setIsListening] = useState(false)
  const [isSpeechSupported, setIsSpeechSupported] = useState(!!transcribeAudio)
  const [isRecording, setIsRecording] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null)
  const activeRecordingRef = useRef<Promise<Blob> | null>(null)
  

  useEffect(() => {
    const checkSpeechSupport = async () => {
      const hasMediaDevices = !!(
        navigator.mediaDevices && navigator.mediaDevices.getUserMedia
      )
      setIsSpeechSupported(hasMediaDevices && !!transcribeAudio)
    }

    checkSpeechSupport()
  }, [transcribeAudio])

  const stopRecording = async () => {
    setIsRecording(false)
    setIsTranscribing(true)
    try {
      // 停止录音并获取录音结果
      const recording = await audioRecorder.stop()
      if (transcribeAudio) {
        const text = await transcribeAudio(recording)
        onTranscriptionComplete?.(text)
      }
    }
    catch (error) {
      // 关闭麦克风
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop())
        setAudioStream(null)
      }
      console.error('Error transcribing audio:', error)
    }
    finally {
      setIsTranscribing(false)
      setIsListening(false)
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop())
        setAudioStream(null)
      }
      activeRecordingRef.current = null
    }
  }

  const startListening = async () => {
    try {
      setIsListening(true)
      setIsRecording(true)
      // Get audio stream first
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      })
      setAudioStream(stream)

      // 如果此时空格键没有按下 则说明用户已经停止录音了
      const isSpacePressed = getDigitalStore().isSpacePressed
      
      if (!isSpacePressed) {
        console.log('检测到用户已经停止录音')
        setIsListening(false)
        setIsRecording(false)
        stream?.getTracks().forEach(track => track.stop())
        setAudioStream(null)
        return
      }

      // Start recording with the stream
      activeRecordingRef.current = audioRecorder.recordAudio(stream)
    }
    catch (error) {
      console.error('Error recording audio:', error)
      setIsListening(false)
      setIsRecording(false)
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop())
        setAudioStream(null)
      }
    }
  }

  return {
    isListening,
    isSpeechSupported,
    isRecording,
    isTranscribing,
    audioStream,
    startListening,
    stopRecording,
  }
}
